<template>
  <layout-with-back-btn title="Performa Mitra Gudang">
    <div class="flex items-center justify-between gap-[10px] flex-wrap my-[24px]">
      <div class="d-flex rounded-lg align-items-center text-[#c2c2c2] px-[12px] py-[3px] border-[1px] border-[#e2e2e2] w-full sm:w-auto">
        <span
          class="k-search-normal-1 font-bold text-[24px] align-middle mx-[5px]"
        />
        <BFormInput
          v-model="warehouse_name"
          placeholder="Cari Nama Mitra"
          class="w-100 border-0 focus:!shadow-none !rounded-r-lg !text-[16px]"
          debounce="1000"
        />
      </div>
      <div class="flex items-center gap-[8px] flex-wrap">
        <date-picker v-model="period">
          <template #default="{data}">
            <div class="d-flex justify-between gap-[14px] items-center w-100 p-[3px]">
              <span class="text-[14px] text-[#c2c2c2]">
                {{ `${DAY_MONTH_YEAR(data.startDate)} - ${DAY_MONTH_YEAR(data.endDate)}` }}
              </span>
              <span class="k-calendar text-[24px]" />
            </div>
          </template>
        </date-picker>
        <b-button
          variant="outline-primary"
          size="sm"
          class="!flex items-center justify-center gap-5 inline-block !text-[16px] !font-[400] !rounded-lg h-[41px] w-full sm:w-auto"
          :disabled="loadingDownloadPerforma"
          @click="downloadPerforma"
        >
          <b-spinner
            v-if="loadingDownloadPerforma"
            small
          />
          Export Data
        </b-button>
      </div>
    </div>

    <b-overlay
      :show="loading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <BTable
        :items="mitra"
        :fields="performaMitraFields"
        class="text-[12px]"
        sticky-header="500px"
        show-empty
        empty-text="Tidak ada data yang ditampilkan."
        responsive
        @scroll.native="handleScrollMitra"
      >
        <template #cell(no)="row">
          {{ row.index + 1 }}
        </template>
        <template #cell(warehouse_name)="row">
          <div class="flex gap-[8px]">
            <img
              v-if="!row.item.warehouse_image"
              src="https://storage.googleapis.com/komerce/assets/icons/product-placehold.svg"
              alt="fotoGudang"
              class="h-[38px] w-[38px]"
            >
            <img
              v-else
              :src="row.item.warehouse_image"
              alt="fotoGudang"
              class="h-[38px] w-[38px] object-cover"
              loading="lazy"
            >
            <div class="">
              <div class="text-[#222222]">
                {{ row.item.warehouse_name }}
              </div>
              <div class="text-primary text-[12px ]">
                {{ row.item.mitra_name }}
              </div>
            </div>
          </div>
        </template>
      </BTable>
    </b-overlay>
  </layout-with-back-btn>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue'
import {
  firstDateOfMonth,
  lastDateOfMonth,
  last30,
  last7,
  today,
} from '@/store/helpers'
import {
  LABELDAY,
  LABELMONTH,
  DAY_MONTH_YEAR,
  YEAR_MONTH_DAY,
} from '@/libs/filterDate'
import LayoutWithBackBtn from '../../LayoutWithBackBtn.vue'
import DatePicker from '../../DatePicker.vue'
import { performaMitraFields } from './config'

/**
 * @component PerformaMitra
 * @description Component for displaying and managing warehouse partner performance data
 */
export default {
  components: {
    DatePicker,
    LayoutWithBackBtn,
  },
  data() {
    return {
      performaMitraFields,
      firstDateOfMonth,
      last30,
      last7,
      today,
      YEAR_MONTH_DAY,
      DAY_MONTH_YEAR,
      loading: true,
      offset: 0,
      limit: 10,
      warehouse_name: '',
      lastData: false,
      mitra: [],
      period: {
        startDate: firstDateOfMonth,
        endDate: lastDateOfMonth,
      },
      loadingDownloadPerforma: false,
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
      },
      ranges: {
        '7 Hari Terakhir': [last7, today],
        '1 Bulan terakhir': [last30, today],
        'Bulan ini': [firstDateOfMonth, lastDateOfMonth],
        'Custom Tanggal ': [today, today],
      },
    }
  },
  watch: {
    warehouse_name() {
      this.getPerformaMitra()
    },
    period() {
      this.getPerformaMitra()
    },
  },
  created() {
    this.getPerformaMitra()
  },
  methods: {
    /**
     * @description Fetches initial warehouse partner performance data
     * @returns {Promise<void>}
     */
    async getPerformaMitra() {
      this.loading = true
      this.offset = 0
      const url = 'kompack/api/v1/dashboard/admin/performances/mitra'
      const params = {
        limit: this.limit,
        offset: this.offset,
        warehouse_name: this.warehouse_name,
        start_date: YEAR_MONTH_DAY(this.period.startDate),
        end_date: YEAR_MONTH_DAY(this.period.endDate),
      }
      await newAxiosIns
        .get(url, { params })
        .then(res => {
          const { data } = res.data

          this.mitra = data
          this.loading = false

          this.lastData = !data || data.length < this.limit
          if (!this.lastData) {
            this.offset += data.length
          }
        })
        .catch(err => {
          this.loading = false
          this.$toast(
            {
              component: ToastificationContentVue,
              props: {
                title: 'Failure',
                icon: 'AlertCircleIcon',
                text: err.response.message,
                variant: 'danger',
              },
            },
            { timeout: 2000 },
          )
        })
    },
    /**
     * @description Fetches next batch of warehouse partner performance data for infinite scroll
     * @returns {Promise<void>}
     */
    async getNextDataPerformaMitra() {
      this.loading = true
      const url = 'kompack/api/v1/dashboard/admin/performances/mitra'
      const params = {
        limit: this.limit,
        offset: this.offset,
        warehouse_name: this.warehouse_name,
        start_date: YEAR_MONTH_DAY(this.period.startDate),
        end_date: YEAR_MONTH_DAY(this.period.endDate),
      }
      newAxiosIns
        .get(url, { params })
        .then(res => {
          const { data } = res.data

          this.mitra.push(...data)
          this.offset += data.length

          if (data.length < this.limit) {
            this.lastData = true
          }

          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.$toast(
            {
              component: ToastificationContentVue,
              props: {
                title: 'Failure',
                icon: 'AlertCircleIcon',
                text: err.response.message,
                variant: 'danger',
              },
            },
            { timeout: 2000 },
          )
        })
    },
    /**
     * @description Handles scroll event for infinite scrolling of the mitra table
     * @param {Event} event - The scroll event
     */
    handleScrollMitra(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target
      if (
        scrollTop + clientHeight >= scrollHeight - 1 && !this.lastData
      ) {
        this.getNextDataPerformaMitra()
      }
    },
    /**
     * @description Downloads warehouse partner performance data as Excel file
     * @returns {Promise<void>}
     */
    async downloadPerforma() {
      this.loadingDownloadPerforma = true
      const apiUrl = 'kompack/api/v1/dashboard/admin/performances/mitra/download'
      const body = {
        start_date: YEAR_MONTH_DAY(this.period.startDate),
        end_date: YEAR_MONTH_DAY(this.period.endDate),
      }
      await newAxiosIns.post(apiUrl, body, { responseType: 'blob' })
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `performa_mitra_${DAY_MONTH_YEAR(this.period.startDate)} - ${DAY_MONTH_YEAR(this.period.endDate)}.xlsx`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          this.loadingDownloadPerforma = false
        })
        .catch(err => {
          this.loadingDownloadPerforma = false
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: 'Failure',
              icon: 'AlertCircleIcon',
              text: err.response.message,
              variant: 'danger',
            },
          }, { timeout: 2000 })
        })
    },
  },
}
</script>
